/**
 * StandardConfigJsonEditor
 * For Media Devoted | Created By Ciro Autiero
 *
 * Free-form JSON editor for the long tail of `flow_FlowUpdateResource`:
 * paths, conditional path groups, all 20 path conditions, lander/offer
 * weight overrides, smart rotation, listicle, etc. The backend rejects
 * malformed bodies so this is a power-user fallback paired with the
 * structured fields above.
 */

import { FC, useEffect, useState } from 'react';

export interface StandardConfigJsonEditorProps {
  standardJson: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

const StandardConfigJsonEditor: FC<StandardConfigJsonEditorProps> = ({
  standardJson,
  onChange,
}) => {
  const [draft, setDraft] = useState<string>(formatJson(standardJson));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(formatJson(standardJson));
  }, [standardJson]);

  const handleBlur = () => {
    try {
      const next = draft.trim() ? JSON.parse(draft) : {};
      if (!isPlainObject(next)) throw new Error('Root must be an object.');
      setError(null);
      onChange(next as Record<string, unknown>);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-foreground">flow_FlowUpdateResource (JSON)</p>
      <p className="text-xs text-muted-foreground">
        Paths, conditional groups, conditions (country, browser, device, time-of-day, etc.),
        landers, offers, and other Voluum fields.
      </p>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        className="h-72 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
        spellCheck={false}
      />
      {error ? <p className="text-xs text-destructive">JSON error: {error}</p> : null}
    </div>
  );
};

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export default StandardConfigJsonEditor;
